import React, { useRef, ReactElement, useLayoutEffect, useState } from "react";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { StepIndicatorProps, StepIndicatorType } from "./step-indicator.types";
import { Step } from "./step";

export default function MeasureHelper({
  stepIndicatorChildren,
  type,
  totalStepCount,
  setIsCondensed,
  setIsDropdown,
  id,
}: {
  stepIndicatorChildren: StepIndicatorProps["children"];
  type: StepIndicatorProps["type"];
  totalStepCount: number;
  setIsCondensed: (value: boolean) => void;
  setIsDropdown: (value: boolean) => void;
  id?: string;
}): ReactElement {
  if (typeof window !== "undefined" && !window.ResizeObserver) {
    window.ResizeObserver = ResizeObserverPolyfill;
  }

  const [isLarge, setIsLarge] = useState<boolean>(true);

  const dummyLargeRef = useRef<HTMLDivElement>(null);
  const dummyCondensedRef = useRef<HTMLDivElement>(null);

  // Comparing the width of list and total step items for large variant
  useLayoutEffect(() => {
    const handleResizeLarge = (entries: ResizeObserverEntry[]): void => {
      for (const entry of entries) {
        const BREAKPOINT_THRESHOLD = 5;
        const containerWidth = entry.contentRect.width;

        let allItemsWidth = 0;
        Array.from(entry.target.children).forEach((child) => {
          allItemsWidth += (child as HTMLElement).getBoundingClientRect().width;
        });

        if (allItemsWidth > containerWidth + BREAKPOINT_THRESHOLD) {
          setIsCondensed(true);
          setIsLarge(false);
        } else {
          setIsCondensed(false);
          setIsLarge(true);
        }
      }
    };

    const dummyLargeEl = dummyLargeRef.current as Element;
    let resizeObserver: ResizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) =>
        handleResizeLarge(entries)
      );

      resizeObserver.observe(dummyLargeEl);
    }

    // Clean resizeObserver
    return () => {
      if (typeof ResizeObserver !== "undefined") {
        if (dummyLargeEl) {
          resizeObserver.unobserve(dummyLargeEl);
        } else {
          resizeObserver.disconnect();
        }
      }
    };
    /**
     * totalStepCount added as an additional dep to ensure the width is
     * recalculated when the number of steps is changed
     */
  }, [setIsCondensed, totalStepCount]);

  // Comparing the width of list and total step items for condensed variant
  useLayoutEffect(() => {
    const handleResizeCondensed = (entries: ResizeObserverEntry[]): void => {
      if (type === StepIndicatorType.STANDARD_DROPDOWN) {
        /**
         * Condensed variant do not require label/sublabels width calculations,
         * so we are relying on single item width information
         */
        for (const entry of entries) {
          const singleItemWidth =
            entry.target.children[0].getBoundingClientRect().width;
          const containerWidth = entry.contentRect.width;
          const allItemsWidth = totalStepCount * singleItemWidth;
          if (allItemsWidth > containerWidth) {
            setIsCondensed(false);
            setIsDropdown(true);
          } else {
            setIsDropdown(false);
            if (!isLarge) {
              setIsCondensed(true);
            }
          }
        }
      }
    };

    const dummyCondensedEl = dummyCondensedRef.current as Element;
    let resizeObserver: ResizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) =>
        handleResizeCondensed(entries)
      );

      resizeObserver.observe(dummyCondensedEl);

      // Clean resizeObserver
      return () => {
        if (typeof ResizeObserver !== "undefined") {
          if (dummyCondensedEl) {
            resizeObserver.unobserve(dummyCondensedEl);
          } else {
            resizeObserver.disconnect();
          }
        }
      };
    }
  }, [isLarge, setIsDropdown, setIsCondensed, totalStepCount, type]);

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 100,
        height: 0,
        width: "100%",
        overflow: "hidden",
        visibility: "hidden",
      }}
    >
      {/* Dummy condensed variant */}
      <div
        className="spark-step-indicator__list spark-step-indicator__list--condensed"
        ref={dummyCondensedRef}
      >
        {Array.from({ length: totalStepCount }).map((_, i) => (
          <Step
            key={i}
            label=""
            id={id ? `${id}_step_${i}` : undefined}
            tabIndex={-1}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Dummy large variant */}
      <div className="spark-step-indicator__list" ref={dummyLargeRef}>
        {React.Children.map(stepIndicatorChildren, (child, i) => {
          if (!React.isValidElement(child)) {
            return null;
          }

          const childType: any = child.type;
          if (childType.displayName === "Step") {
            return (
              <Step
                key={i}
                id={child.props.id}
                label={child.props.label}
                subLabel={child.props.subLabel}
                tabIndex={-1}
                aria-hidden="true"
              />
            );
          }
        })}
      </div>
    </div>
  );
}
